/**
 * WorkflowsPage — manage workflow automation rules.
 */

import { useEffect, useState } from "react";
import type { ActionType, TriggerType, WorkflowAction, WorkflowCondition, WorkflowRule } from "../../types/workflows";
import { apiFetch } from "../../lib/config";

const TRIGGER_TYPES: { value: TriggerType; label: string }[] = [
  { value: "incident_created", label: "Incident Created" },
  { value: "incident_resolved", label: "Incident Resolved" },
  { value: "alert_fired", label: "Alert Fired" },
  { value: "monitor_down", label: "Monitor Down" },
  { value: "monitor_recovered", label: "Monitor Recovered" },
  { value: "schedule", label: "Schedule (cron)" },
  { value: "manual", label: "Manual" },
];

const ACTION_TYPES: { value: ActionType; label: string }[] = [
  { value: "send_email", label: "Send Email" },
  { value: "send_webhook", label: "Send Webhook" },
  { value: "create_incident", label: "Create Incident" },
  { value: "acknowledge_alert", label: "Acknowledge Alert" },
  { value: "page_on_call", label: "Page On-Call" },
];

const CONDITION_OPS = ["eq", "neq", "gt", "lt", "gte", "lte", "contains", "not_contains", "in"];

const EXEC_STATUS_BADGE: Record<string, string> = {
  success: "ac-badge--operational",
  failed: "ac-badge--outage",
  skipped: "ac-badge--neutral",
  pending: "ac-badge--degraded",
  running: "ac-badge--info",
};

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

function buildEmptyAction(): WorkflowAction {
  return { type: "send_email", config: {} };
}

function buildEmptyCondition(): WorkflowCondition {
  return { field: "", op: "eq", value: "" };
}

export function WorkflowsPage() {
  const [rules, setRules] = useState<WorkflowRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formTrigger, setFormTrigger] = useState<TriggerType>("incident_created");
  const [formTriggerConfig, setFormTriggerConfig] = useState<Record<string, string>>({});
  const [formConditions, setFormConditions] = useState<WorkflowCondition[]>([]);
  const [formActions, setFormActions] = useState<WorkflowAction[]>([buildEmptyAction()]);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [triggerResult, setTriggerResult] = useState<Record<string, string>>({});

  const load = async () => {
    try {
      setLoading(true);
      const data = await fetchJSON<WorkflowRule[]>("/api/v1/workflows/");
      setRules(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setFormName(""); setFormDesc(""); setFormTrigger("incident_created");
    setFormTriggerConfig({}); setFormConditions([]); setFormActions([buildEmptyAction()]);
    setFormError(null); setEditId(null);
  };

  const openEdit = (rule: WorkflowRule) => {
    setFormName(rule.name); setFormDesc(rule.description);
    setFormTrigger(rule.trigger_type);
    setFormTriggerConfig(Object.fromEntries(Object.entries(rule.trigger_config).map(([k, v]) => [k, String(v)])));
    setFormConditions(rule.conditions); setFormActions(rule.actions);
    setEditId(rule.id); setShowCreate(true);
  };

  const buildPayload = () => ({
    name: formName.trim(), description: formDesc,
    trigger_type: formTrigger, trigger_config: formTriggerConfig,
    conditions: formConditions, actions: formActions,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setFormError(null);
    if (!formName.trim()) { setFormError("Name is required."); return; }
    if (formActions.length === 0) { setFormError("At least one action is required."); return; }
    setSubmitting(true);
    try {
      if (editId) {
        await fetchJSON(`/api/v1/workflows/${editId}/`, { method: "PATCH", body: JSON.stringify(buildPayload()) });
      } else {
        await fetchJSON("/api/v1/workflows/", { method: "POST", body: JSON.stringify(buildPayload()) });
      }
      setShowCreate(false); resetForm(); await load();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Submit failed");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id: string) => {
    await apiFetch(`/api/v1/workflows/${id}/`, { method: "DELETE" });
    setDeletingId(null); await load();
  };

  const handleToggle = async (rule: WorkflowRule) => {
    await fetchJSON(`/api/v1/workflows/${rule.id}/`, {
      method: "PATCH", body: JSON.stringify({ is_active: !rule.is_active }),
    });
    await load();
  };

  const handleTrigger = async (rule: WorkflowRule) => {
    try {
      const result = await fetchJSON<{ status: string }>(`/api/v1/workflows/${rule.id}/trigger/`, {
        method: "POST", body: JSON.stringify({}),
      });
      setTriggerResult((prev) => ({ ...prev, [rule.id]: result.status }));
    } catch {
      setTriggerResult((prev) => ({ ...prev, [rule.id]: "error" }));
    }
  };

  const updateCondition = (i: number, field: string, value: string) => {
    setFormConditions((prev) => prev.map((c, idx) => idx === i ? { ...c, [field]: value } : c));
  };

  const updateAction = (i: number, field: string, value: string) => {
    setFormActions((prev) => prev.map((a, idx) => {
      if (idx !== i) return a;
      if (field === "type") return { type: value as ActionType, config: {} };
      return { ...a, config: { ...a.config, [field]: value } };
    }));
  };

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Workflows</h1>
          <p className="ac-page-subtitle">Automate actions based on platform events and schedules</p>
        </div>
        <div className="ac-page-header__actions">
          <button className="aptean-btn aptean-btn--primary" onClick={() => { resetForm(); setShowCreate(v => !v); }}>
            {showCreate && !editId ? "Cancel" : "+ Create Rule"}
          </button>
        </div>
      </div>

      {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "var(--aptean-text-secondary)" }}>Loading workflows...</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: "30%" }}>Rule Name</th>
                <th>Trigger</th>
                <th>Actions</th>
                <th>Status</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr><td colSpan={5}><div className="ac-empty"><div className="ac-empty__title">No workflow rules</div><div className="ac-empty__body">Create a rule to automate platform responses.</div></div></td></tr>
              ) : rules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <div className="ac-row-title">{rule.name}</div>
                    {rule.description && <div className="ac-row-sub">{rule.description}</div>}
                  </td>
                  <td><span className="ac-badge ac-badge--neutral">{TRIGGER_TYPES.find(t => t.value === rule.trigger_type)?.label ?? rule.trigger_type}</span></td>
                  <td className="ac-td--muted">{rule.actions.length} action{rule.actions.length !== 1 ? "s" : ""}</td>
                  <td>
                    <span className={`ac-badge ${rule.is_active ? "ac-badge--operational" : "ac-badge--neutral"}`}>
                      {rule.is_active ? "Active" : "Inactive"}
                    </span>
                    {triggerResult[rule.id] && (
                      <span className={`ac-badge ${EXEC_STATUS_BADGE[triggerResult[rule.id]] ?? "ac-badge--neutral"}`} style={{ marginLeft: "0.375rem" }}>
                        {triggerResult[rule.id]}
                      </span>
                    )}
                  </td>
                  <td className="ac-td--right ac-td--actions">
                    <div style={{ display: "flex", gap: "0.375rem", justifyContent: "flex-end" }}>
                      <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.625rem" }} onClick={() => handleToggle(rule)}>
                        {rule.is_active ? "Disable" : "Enable"}
                      </button>
                      <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.625rem" }} onClick={() => openEdit(rule)}>Edit</button>
                      <button type="button" className="aptean-btn aptean-btn--secondary" style={{ fontSize: "0.75rem", padding: "0.3rem 0.625rem" }} onClick={() => handleTrigger(rule)}>Trigger</button>
                      <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.625rem", color: "#a4262c", borderColor: "#f1a0a3" }} onClick={() => setDeletingId(rule.id)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && (setShowCreate(false), resetForm())}>
          <div className="ac-dialog ac-dialog--wide">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">{editId ? "Edit Rule" : "Create Rule"}</h2>
              <button type="button" className="ac-dialog__close" onClick={() => { setShowCreate(false); resetForm(); }}>&#10005;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="ac-dialog__body">
                {formError && <div className="ac-alert ac-alert--error">{formError}</div>}
                <div className="ac-field-row">
                  <div className="ac-field">
                    <label>Name *</label>
                    <input className="aptean-input" value={formName} onChange={(e) => setFormName(e.target.value)} required />
                  </div>
                  <div className="ac-field">
                    <label>Trigger Type</label>
                    <select className="aptean-input" value={formTrigger} onChange={(e) => { setFormTrigger(e.target.value as TriggerType); setFormTriggerConfig({}); }}>
                      {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="ac-field">
                  <label>Description</label>
                  <input className="aptean-input" value={formDesc} onChange={(e) => setFormDesc(e.target.value)} />
                </div>
                {formTrigger === "schedule" && (
                  <div className="ac-field">
                    <label>Cron expression</label>
                    <input className="aptean-input" value={formTriggerConfig.cron ?? ""} onChange={(e) => setFormTriggerConfig({ cron: e.target.value })} placeholder="*/15 * * * *" data-testid="cron-field" />
                  </div>
                )}

                <div style={{ borderTop: "1px solid var(--aptean-border-default)", paddingTop: "0.875rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <div style={{ fontSize: "0.8125rem", fontWeight: 700, color: "var(--aptean-text-primary)" }}>Conditions</div>
                    <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.2rem 0.625rem" }} onClick={() => setFormConditions(p => [...p, buildEmptyCondition()])}>+ Add</button>
                  </div>
                  {formConditions.map((c, i) => (
                    <div key={i} style={{ display: "flex", gap: "0.5rem", marginBottom: "0.5rem", alignItems: "center" }}>
                      <input className="aptean-input" placeholder="field" value={c.field} onChange={(e) => updateCondition(i, "field", e.target.value)} style={{ flex: 1 }} />
                      <select className="aptean-input" value={c.op} onChange={(e) => updateCondition(i, "op", e.target.value)} style={{ flex: 1 }}>
                        {CONDITION_OPS.map((op) => <option key={op} value={op}>{op}</option>)}
                      </select>
                      <input className="aptean-input" placeholder="value" value={String(c.value)} onChange={(e) => updateCondition(i, "value", e.target.value)} style={{ flex: 1 }} />
                      <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", color: "#a4262c" }} onClick={() => setFormConditions(p => p.filter((_, idx) => idx !== i))}>Remove</button>
                    </div>
                  ))}
                </div>

                <div style={{ borderTop: "1px solid var(--aptean-border-default)", paddingTop: "0.875rem" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <div style={{ fontSize: "0.8125rem", fontWeight: 700, color: "var(--aptean-text-primary)" }}>Actions *</div>
                    <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.2rem 0.625rem" }} onClick={() => setFormActions(p => [...p, buildEmptyAction()])}>+ Add</button>
                  </div>
                  {formActions.map((a, i) => (
                    <div key={i} style={{ padding: "0.75rem", background: "var(--aptean-bg-overlay)", borderRadius: "var(--aptean-radius-md)", marginBottom: "0.5rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <select className="aptean-input" value={a.type} onChange={(e) => updateAction(i, "type", e.target.value)} style={{ flex: 1 }}>
                          {ACTION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", color: "#a4262c" }} onClick={() => setFormActions(p => p.filter((_, idx) => idx !== i))}>Remove</button>
                      </div>
                      {a.type === "send_email" && (
                        <>
                          <input className="aptean-input" placeholder="To (comma-separated emails)" value={String(a.config.to ?? "")} onChange={(e) => updateAction(i, "to", e.target.value)} data-testid="email-config-field" />
                          <input className="aptean-input" placeholder="Subject" value={String(a.config.subject ?? "")} onChange={(e) => updateAction(i, "subject", e.target.value)} />
                          <input className="aptean-input" placeholder="Body template" value={String(a.config.body ?? "")} onChange={(e) => updateAction(i, "body", e.target.value)} />
                        </>
                      )}
                      {a.type === "send_webhook" && (
                        <input className="aptean-input" placeholder="Webhook URL" value={String(a.config.url ?? "")} onChange={(e) => updateAction(i, "url", e.target.value)} />
                      )}
                      {a.type === "create_incident" && (
                        <>
                          <input className="aptean-input" placeholder="Title template" value={String(a.config.title ?? "")} onChange={(e) => updateAction(i, "title", e.target.value)} />
                          <select className="aptean-input" value={String(a.config.severity ?? "medium")} onChange={(e) => updateAction(i, "severity", e.target.value)}>
                            {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => { setShowCreate(false); resetForm(); }}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={submitting}>{submitting ? "Saving..." : editId ? "Save" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletingId && (
        <div className="ac-overlay">
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Confirm Delete</h2>
            </div>
            <div className="ac-dialog__body">
              <div className="ac-alert ac-alert--warning">Delete this workflow rule? This cannot be undone.</div>
            </div>
            <div className="ac-dialog__footer">
              <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setDeletingId(null)}>Cancel</button>
              <button type="button" className="aptean-btn aptean-btn--primary" style={{ background: "#a4262c", borderColor: "#a4262c" }} onClick={() => handleDelete(deletingId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
